import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Building2, Award, Target, Eye, Heart, Download, Calendar, MapPin, Users, CheckCircle } from "lucide-react";
const About = () => {
  const values = ["Transparência", "Agregar valor", "Alta performance", "Renovação", "Responsabilidade", "Sustentabilidade", "\"Ser\" humano", "Visão de dono"];
  const milestones = [{
    year: "2008",
    event: "Fundação da empresa em Arcos, MG",
    icon: Building2
  }, {
    year: "2016",
    event: "Especialização em cal dolomítica virgem",
    icon: Target
  }, {
    year: "2018",
    event: "Início das operações em Colombo, PR",
    icon: Users
  }, {
    year: "2022",
    event: "Expansão para o Nordeste - Euclides da Cunha, BA",
    icon: MapPin
  }, {
    year: "2025",
    event: "18 anos de trajetória - Presença nacional",
    icon: Award
  }];
  return <div className="min-h-screen bg-gradient-subtle">
      <Seo
        title="Sobre a Quimical: história e qualidade em cal"
        description="Conheça a história, missão, valores e compromisso com a qualidade da Quimical, referência em cal virgem e hidratada com atuação nacional."
        canonical="https://quimical.com.br/about"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-lg font-medium text-muted-foreground">A Quimical tem 18 anos de história</h2>
          <h1 className="text-4xl md:text-6xl font-bold text-black mt-2 mb-4">Sobre Nós</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            18 anos de excelência no setor químico, construindo parcerias sólidas e
            oferecendo soluções de alta performance para o mercado brasileiro.
          </p>
        </div>

        {/* Nossa História */}
        <Card className="mb-12 shadow-elegant">
          <CardHeader className="bg-gradient-primary text-black rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl text-black">Nossa História</CardTitle>
                <CardDescription className="text-black/80">
                  18 anos de crescimento e inovação
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <div className="prose prose-lg max-w-none text-foreground">
              <p className="mb-6">
                Em 2025, a Quimical celebra <strong>18 anos de trajetória marcante</strong> no setor químico.
              </p>
              <p className="mb-6">
                Fundada em 2008 em Arcos, Minas Gerais, a empresa se especializou em 2016 na distribuição de 
                cal dolomítica virgem, focando em clientes siderúrgicos. Expandiu sua atuação em 2018 ao firmar 
                uma parceria com um produtor de cal dolomítica virgem e hidratada em Colombo, Paraná. Em 2022, 
                estendeu suas operações para o Nordeste, inaugurando as operações comerciais em Euclides da Cunha, 
                Bahia. Atualmente, tem um alcance nacional, com instalações estrategicamente localizadas para 
                minimizar os custos de transporte até os principais centros consumidores.
              </p>
              <p className="mb-8">
                Seu portfólio diversificado inclui cal virgem calcítica e dolomítica, cal hidratada e serviços de transporte rodoviário de cargas.
              </p>
            </div>

            {/* Timeline */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-6 text-center text-black">Marcos Importantes</h3>
              <div className="space-y-4">
                {milestones.map((milestone, index) => {
                const Icon = milestone.icon;
                return <div key={index} className="flex items-center space-x-4 p-4 bg-gradient-subtle rounded-lg">
                      <div className="bg-primary text-white p-3 rounded-full">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <Badge variant="secondary" className="mb-2">{milestone.year}</Badge>
                        <p className="font-medium">{milestone.event}</p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8">
              <Button asChild variant="outline" className="shadow-soft">
                <a href="/media/Ficha%20Cadastral.pdf" download>
                  <Download className="h-4 w-4 mr-2" />
                  Ficha Cadastral
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Certificação ISO 9001:2015 */}
        <Card className="mb-12 shadow-elegant">
          <CardHeader className="bg-gradient-accent text-black rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Award className="h-8 w-8" aria-hidden="true" />
              <div>
                <CardTitle className="text-2xl text-black">Certificação ISO 9001:2015</CardTitle>
                <CardDescription className="text-black/80">
                  Sistema de Gestão da Qualidade certificado pela CBG
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8 space-y-4">
            <p className="text-foreground leading-relaxed">
              A Empresa Brasileira de Mercadologia LTDA possui certificação de seu Sistema de Gestão
              da Qualidade conforme a norma ABNT NBR ISO 9001:2015, concedida pela
              CBG – Certificadora Brasileira de Gestão.
            </p>
            <dl className="grid gap-4 text-sm sm:grid-cols-2">
              <div><dt className="text-muted-foreground">Certificação inicial</dt><dd className="font-medium">14/12/2018</dd></div>
              <div><dt className="text-muted-foreground">Validade do certificado apresentado</dt><dd className="font-medium">22/11/2026</dd></div>
              <div><dt className="text-muted-foreground">Término do ciclo de certificação</dt><dd className="font-medium">22/11/2027</dd></div>
              <div><dt className="text-muted-foreground">Registro</dt><dd className="font-medium">IQ–1541/2022 – Revisão 1</dd></div>
            </dl>
            <Button asChild variant="outline" className="shadow-soft">
              <a href="/media/QUIMICAL%20-%20CERTIFICADO%20DA%20ISO%209001%20-%20REVISAR%20ATE%2022%2011%202026.pdf" download>
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                Baixar certificado ISO 9001:2015
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Política de Qualidade */}
        <Card className="mb-12 shadow-elegant">
          <CardHeader className="bg-gradient-accent text-black rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Award className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl text-black">Nossa Política de Qualidade</CardTitle>
                <CardDescription className="text-black/80">
                  Comprometimento com a excelência
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <div className="prose prose-lg max-w-none text-foreground">
              <p className="text-lg font-medium mb-6 text-industrial-blue">
                Levamos a sério nossa política de qualidade.
              </p>
              <p className="mb-6">
                A Empresa Brasileira de Mercadologia LTDA, se compromete em: pesquisar, conhecer, 
                entender e antecipar as necessidades dos clientes internos e externos para superá-las 
                metodicamente e continuamente, e em capacitar e sensibilizar indivíduos e equipes, 
                para que compreendam o impacto das suas contribuições em atender aos requisitos legais 
                aplicáveis, além de:
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start space-x-3 p-4 bg-gradient-subtle rounded-lg">
                  <CheckCircle className="h-6 w-6 text-industrial-green mt-1" />
                  <p>Comprometer com a melhoria contínua do sistema de gestão da qualidade</p>
                </div>
                <div className="flex items-start space-x-3 p-4 bg-gradient-subtle rounded-lg">
                  <CheckCircle className="h-6 w-6 text-industrial-green mt-1" />
                  <p>Garantir a satisfação do cliente</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end">
              <div className="text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 inline mr-2" />
                Versão 3 - 12 de maio de 2022
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Missão, Visão e Valores */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Missão */}
          <Card className="shadow-elegant">
            <CardHeader className="bg-gradient-secondary text-black rounded-t-lg">
              <div className="flex items-center space-x-2">
                <Target className="h-6 w-6" />
                <CardTitle className="text-black">Nossa Missão</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-sm leading-relaxed">
                Prestar serviços de suporte a gestão comercial e logística a pessoas jurídicas, 
                auxiliando-as no desenvolvimento de soluções customizadas para atender as necessidades 
                do mercado consumidor, com ênfase em sustentabilidade ambiental, econômica e 
                principalmente técnica.
              </p>
            </CardContent>
          </Card>

          {/* Visão */}
          <Card className="shadow-elegant">
            <CardHeader className="bg-gradient-primary text-black rounded-t-lg">
              <div className="flex items-center space-x-2">
                <Eye className="h-6 w-6" />
                <CardTitle className="text-black">Nossa Visão</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-sm leading-relaxed">
                Ser referência para clientes, fornecedores e colaboradores na prestação de serviços 
                a gestão comercial e logística.
              </p>
            </CardContent>
          </Card>

          {/* Valores */}
          <Card className="shadow-elegant">
            <CardHeader className="bg-gradient-accent text-black rounded-t-lg">
              <div className="flex items-center space-x-2">
                <Heart className="h-6 w-6" />
                <CardTitle className="text-black">Nossos Valores</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-xs mb-4 text-muted-foreground">
                Nossos valores corporativos, pois tudo envolve uma escolha!
              </p>
              <div className="space-y-2">
                {values.map((value, index) => <Badge key={index} variant="secondary" className="mr-2 mb-2 text-xs bg-gradient-subtle">
                    {value}
                  </Badge>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Código de Ética */}
        <Card className="shadow-elegant">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-black">Código de Ética</h3>
              <p className="text-muted-foreground mt-2">
                Nossos princípios e diretrizes éticas
              </p>
            </div>
            <div className="flex items-center justify-center space-x-4">
              <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-soft">
                <a href="https://workdrive.zohoexternal.com/external/writer/f35b548e635aef38583938925c65a260018532b278be8482593ecbe31402d68f" target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  Conheça nosso código de ética
                </a>
              </Button>
              <div className="text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 inline mr-2" />
                Versão 1 - 28/06/2018
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>;
};
export default About;
